import {
  ChannelMemberSeqWithTenant,
  CreatePrivateChannelParam,
  GenerateSharedChannelTokenResponse,
  TokenDetail,
} from '@@types/Api/Channel';
import { InviteMemberResponse, RemoveMemberResponse } from '@@types/Api/Member';
import Channel, { SharedChannel, SharedTenantInfo } from '@@types/Channel';
import { CHANNEL_ID, MEMBER_ID, RawWrap, TENANT_ID, UUID } from '@@types/common';
import { RemoveMemberRequest } from '@@types/Member';

import { ChannelType, URLS } from '~/constants';
import InterTenantApi from '~/stores/providers/InterTenantApi';
import { getResult, isSuccessful } from '~/utils/apiUtil';
import { curry } from 'ramda';

class InterTenantChannelApi {
  constructor(private api: InterTenantApi) {}

  fetchChannels = (): Promise<SharedChannel[]> => {
    return this.api.ignoreErrorRequester.get(`${URLS.V4_API}/channels`).then(getResult) as Promise<SharedChannel[]>;
  };

  fetchChannel = (channelId: CHANNEL_ID): Promise<Channel> => {
    return this.api.contentsRequester.get(`${URLS.V4_API}/channels/${channelId}`);
  };

  fetchChannelReadSeq = (channelId: CHANNEL_ID): Promise<ChannelMemberSeqWithTenant[]> => {
    return this.api.resultRequester.cancelGet(`${URLS.V4_API}/channels/${channelId}/members/seq`, channelId);
  };

  fetchChannelTenantList = (channelId: CHANNEL_ID): Promise<SharedTenantInfo[]> => {
    return this.api.resultRequester.cancelGet(`${URLS.V4_API}/channels/${channelId}/shared-tenants`, channelId);
  };

  generateSharedChannelToken = (
    tenantId: TENANT_ID,
    channelId: CHANNEL_ID
  ): Promise<GenerateSharedChannelTokenResponse> => {
    return this.api.resultRequester.post(`${URLS.V4_API}/channels/${channelId}/tokens`, {
      tenantId,
    });
  };

  getTokenDetail = (token: UUID): Promise<TokenDetail> => {
    return this.api.resultRequester.get(`${URLS.V4_API}/tokens/${token}`);
  };

  joinSharedChannel = (token: UUID, channelTitle: string): Promise<void> => {
    return this.api.resultRequester.post(`${URLS.V4_API}/channels/join`, { token, channelTitle });
  };

  updateChannel = curry((channelId: CHANNEL_ID, params: Partial<Channel>) => {
    return this.api.resultRequester.put(`${URLS.V4_API}/channels/${channelId}`, params);
  });

  leaveChannel = (channelId: CHANNEL_ID, memberId: MEMBER_ID): Promise<void> => {
    return this.api.resultRequester.delete(`${URLS.V4_API}/channels/${channelId}/members/${memberId}`);
  };

  inviteChannelMembers = curry(
    (channelId: CHANNEL_ID, tenantId: TENANT_ID, memberIds: MEMBER_ID[]): Promise<InviteMemberResponse> => {
      return this.api.resultRequester.post(`${URLS.V4_API}/channels/${channelId}/members`, {
        tenantId,
        memberIds,
      });
    }
  );

  createTopicChannel = ({
    title = '',
    description = '',
    capacity = 100,
    memberIds = [],
  }: CreatePrivateChannelParam): Promise<SharedChannel> => {
    const params = {
      type: ChannelType.Private,
      title,
      description,
      capacity,
      memberIds,
    };
    return this.api.resultRequester.post(`${URLS.V4_API}/channels/private`, params);
  };

  checkDuplicateTitle = (title: string): Promise<void> => {
    return this.api.ignoreErrorRequester
      .post<RawWrap<void>>(`${URLS.V4_API}/channels/check-title`, {
        title,
      })
      .then((result) => {
        return isSuccessful(result) ? Promise.resolve() : Promise.reject(result);
      });
  };

  removeMembers = (channelId: CHANNEL_ID, members: RemoveMemberRequest[]): Promise<RemoveMemberResponse> => {
    return this.api.resultRequester.post(`${URLS.V4_API}/channels/${channelId}/members/delete`, {
      members,
    });
  };
}

export default InterTenantChannelApi;
